import React, { useMemo, useState, useEffect, isValidElement } from 'react';
import { ChevronDownIcon, ChevronUpIcon, ArrowUpDownIcon, FilterIcon, DownloadIcon, SearchIcon, InboxIcon } from 'lucide-react';
import Button from './Button';
import { TableSkeleton } from './Skeleton';
import EmptyState from './EmptyState';

interface Column<T> {
  key: string;
  header: React.ReactNode;
  cell: (item: T) => React.ReactNode;
  sortable?: boolean;
  filterable?: boolean;
  /** Right-align + monospace tabular figures (codes, quantities, percentages). */
  numeric?: boolean;
  /** Explicit value for CSV export (otherwise derived from the raw value / rendered cell text). */
  csvValue?: (item: T) => string;
}
interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  keyField: keyof T;
  title?: string;
  description?: string;
  pagination?: boolean;
  pageSize?: number;
  actions?: React.ReactNode;
  onRowClick?: (item: T) => void;
  loading?: boolean;
  emptyState?: React.ReactNode;
  className?: string;
  /** Show a global search box that matches across all columns (default true). */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Keep the header visible while the body scrolls (default true). */
  stickyHeader?: boolean;
  /** Opt-in row selection with checkboxes. */
  selectable?: boolean;
  onSelectionChange?: (selected: T[]) => void;
  /** Rendered in a bar above the table while rows are selected. */
  bulkActions?: (selected: T[]) => React.ReactNode;
}

/** Extract plain text from a rendered React cell, for CSV export. */
function reactText(node: React.ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(reactText).join(' ');
  if (isValidElement(node)) return reactText((node.props as { children?: React.ReactNode }).children);
  return '';
}

function DataTable<T>({
  data,
  columns,
  keyField,
  title,
  description,
  pagination = true,
  pageSize = 10,
  actions,
  onRowClick,
  loading = false,
  emptyState,
  className = '',
  searchable = true,
  searchPlaceholder = 'Search…',
  stickyHeader = true,
  selectable = false,
  onSelectionChange,
  bulkActions,
}: DataTableProps<T>) {
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<Set<unknown>>(new Set());

  // Sorting + per-column filters + global search
  const processedData = useMemo(() => {
    let filteredData = [...data];

    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        const lower = value.toLowerCase();
        filteredData = filteredData.filter((item) =>
          String((item as any)[key] ?? '').toLowerCase().includes(lower)
        );
      }
    });

    const q = search.trim().toLowerCase();
    if (q) {
      filteredData = filteredData.filter((item) =>
        columns.some((col) => {
          const raw = (item as any)[col.key];
          const text = raw != null && !isValidElement(raw) ? String(raw) : reactText(col.cell(item));
          return text.toLowerCase().includes(q);
        })
      );
    }

    if (sortConfig) {
      filteredData.sort((a, b) => {
        const aValue = (a as any)[sortConfig.key];
        const bValue = (b as any)[sortConfig.key];
        if (aValue === bValue) return 0;
        if (aValue == null) return sortConfig.direction === 'asc' ? -1 : 1;
        if (bValue == null) return sortConfig.direction === 'asc' ? 1 : -1;
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        }
        return sortConfig.direction === 'asc' ? (aValue > bValue ? 1 : -1) : (aValue < bValue ? 1 : -1);
      });
    }
    return filteredData;
  }, [data, sortConfig, filters, search, columns]);

  // Reset to first page when the result set shrinks under the current page.
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(processedData.length / pageSize) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [processedData.length, pageSize, page]);

  const paginatedData = useMemo(() => {
    if (!pagination) return processedData;
    const start = page * pageSize;
    return processedData.slice(start, start + pageSize);
  }, [processedData, page, pageSize, pagination]);

  const totalPages = useMemo(() => Math.ceil(processedData.length / pageSize), [processedData, pageSize]);

  // Selection
  const selectedItems = useMemo(
    () => data.filter((item) => selectedKeys.has(item[keyField])),
    [data, selectedKeys, keyField]
  );
  useEffect(() => { onSelectionChange?.(selectedItems); }, [selectedItems, onSelectionChange]);

  const toggleRow = (item: T) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      const k = item[keyField];
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });
  };
  const pageAllSelected = paginatedData.length > 0 && paginatedData.every((i) => selectedKeys.has(i[keyField]));
  const togglePageAll = () => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (pageAllSelected) paginatedData.forEach((i) => next.delete(i[keyField]));
      else paginatedData.forEach((i) => next.add(i[keyField]));
      return next;
    });
  };

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (!prev || prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return null;
    });
  };
  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };
  const toggleFilter = (key: string) => {
    setActiveFilters((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const exportCSV = () => {
    const esc = (v: string) => {
      const s = String(v ?? '');
      const escaped = s.replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${escaped}"` : escaped;
    };
    const headers = columns.map((c) => esc(reactText(c.header))).join(',');
    const rows = processedData.map((item) =>
      columns.map((col) => {
        if (col.csvValue) return esc(col.csvValue(item));
        const raw = (item as any)[col.key];
        if (raw != null && !isValidElement(raw)) return esc(String(raw));
        return esc(reactText(col.cell(item)));
      }).join(',')
    ).join('\n');
    const blob = new Blob([`${headers}\n${rows}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `${title || 'data'}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const colSpan = columns.length + (selectable ? 1 : 0);

  return (
    <div className={`bg-white shadow-e1 rounded-lg border border-ink-200 overflow-hidden ${className}`}>
      {/* Header */}
      {(title || actions || searchable) && (
        <div className="px-4 py-4 sm:px-6 border-b border-ink-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="min-w-0">
              {title && <h3 className="text-card-title text-ink-900">{title}</h3>}
              {description && <p className="mt-1 text-body-sm text-ink-500">{description}</p>}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {searchable && (
                <div className="relative">
                  <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400 pointer-events-none" />
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                    placeholder={searchPlaceholder}
                    aria-label="Search table"
                    className="pl-8 pr-3 h-9 text-body-sm border border-ink-300 rounded-md focus:outline-none focus:ring-1 focus:ring-navy-600 focus:border-navy-600 w-40 sm:w-56"
                  />
                </div>
              )}
              <Button variant="secondary" size="sm" leftIcon={<DownloadIcon className="h-4 w-4" />} onClick={exportCSV}>
                Export
              </Button>
              {actions}
            </div>
          </div>
        </div>
      )}

      {/* Bulk action bar */}
      {selectable && selectedItems.length > 0 && (
        <div className="px-4 sm:px-6 py-2.5 bg-navy-50 border-b border-navy-200 flex items-center justify-between gap-3">
          <span className="text-body-sm text-navy-800 font-medium font-mono tabular-nums">
            {selectedItems.length} selected
          </span>
          <div className="flex items-center gap-2">
            {bulkActions?.(selectedItems)}
            <Button variant="ghost" size="sm" onClick={() => setSelectedKeys(new Set())}>Clear</Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-ink-200">
          <thead className={`bg-ink-50 ${stickyHeader ? 'sticky top-0 z-10' : ''}`}>
            <tr>
              {selectable && (
                <th scope="col" className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    aria-label="Select all on page"
                    checked={pageAllSelected}
                    onChange={togglePageAll}
                    className="h-4 w-4 rounded border-ink-300 text-navy-600 focus:ring-navy-600"
                  />
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={`px-6 py-3 text-table-head text-ink-500 ${column.numeric ? 'text-right' : 'text-left'}`}
                >
                  <div className={`flex items-center gap-1 ${column.numeric ? 'justify-end' : ''}`}>
                    <span>{column.header}</span>
                    {column.sortable && (
                      <button onClick={() => handleSort(column.key)} className="ml-1 text-ink-400 hover:text-ink-700" aria-label={`Sort by ${reactText(column.header)}`}>
                        {sortConfig?.key === column.key
                          ? (sortConfig.direction === 'asc' ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />)
                          : <ArrowUpDownIcon className="h-4 w-4" />}
                      </button>
                    )}
                    {column.filterable && (
                      <button onClick={() => toggleFilter(column.key)} className={`ml-1 ${activeFilters.includes(column.key) ? 'text-navy-600' : 'text-ink-400 hover:text-ink-700'}`} aria-label={`Filter ${reactText(column.header)}`}>
                        <FilterIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  {column.filterable && activeFilters.includes(column.key) && (
                    <div className="mt-2">
                      <input
                        type="text"
                        className="block w-full text-body-sm border-ink-300 rounded-md shadow-sm focus:ring-navy-600 focus:border-navy-600"
                        placeholder={`Filter…`}
                        value={filters[column.key] || ''}
                        onChange={(e) => handleFilterChange(column.key, e.target.value)}
                      />
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-ink-200">
            {loading ? (
              <tr>
                <td colSpan={colSpan} className="px-6 py-6">
                  <TableSkeleton rows={pageSize > 8 ? 8 : pageSize} cols={colSpan} />
                </td>
              </tr>
            ) : paginatedData.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="px-6">
                  {emptyState || (
                    <EmptyState
                      icon={<InboxIcon className="h-6 w-6" />}
                      title={search || activeFilters.length ? 'No matches' : 'Nothing here yet'}
                      description={search || activeFilters.length ? 'Try a different search or clear the filters.' : undefined}
                    />
                  )}
                </td>
              </tr>
            ) : (
              paginatedData.map((item) => {
                const isSelected = selectedKeys.has(item[keyField]);
                return (
                  <tr
                    key={String(item[keyField])}
                    className={`${onRowClick ? 'cursor-pointer' : ''} ${isSelected ? 'bg-navy-50' : onRowClick ? 'hover:bg-navy-50' : ''}`}
                    onClick={() => onRowClick && onRowClick(item)}
                  >
                    {selectable && (
                      <td className="px-4 py-4 w-10" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label="Select row"
                          checked={isSelected}
                          onChange={() => toggleRow(item)}
                          className="h-4 w-4 rounded border-ink-300 text-navy-600 focus:ring-navy-600"
                        />
                      </td>
                    )}
                    {columns.map((column) => (
                      <td
                        key={`${String(item[keyField])}-${column.key}`}
                        className={`px-6 py-4 whitespace-nowrap text-body-sm text-ink-700 ${column.numeric ? 'text-right font-mono tabular-nums' : ''}`}
                      >
                        {column.cell(item)}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination && totalPages > 1 && (
        <div className="px-4 py-3 flex items-center justify-between border-t border-ink-200 sm:px-6">
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <p className="text-body-sm text-ink-700">
              Showing <span className="font-mono tabular-nums font-medium">{page * pageSize + 1}</span>–
              <span className="font-mono tabular-nums font-medium">{Math.min((page + 1) * pageSize, processedData.length)}</span>{' '}
              of <span className="font-mono tabular-nums font-medium">{processedData.length}</span>
            </p>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button onClick={() => setPage(0)} disabled={page === 0} className={`relative inline-flex items-center px-2 h-9 rounded-l-md border border-ink-300 bg-white text-body-sm font-medium ${page === 0 ? 'text-ink-300 cursor-not-allowed' : 'text-ink-500 hover:bg-navy-50'}`}>
                <span className="sr-only">First</span>«
              </button>
              <button onClick={() => setPage(page - 1)} disabled={page === 0} className={`relative inline-flex items-center px-2 h-9 border border-ink-300 bg-white text-body-sm font-medium ${page === 0 ? 'text-ink-300 cursor-not-allowed' : 'text-ink-500 hover:bg-navy-50'}`}>
                <span className="sr-only">Previous</span>
                <ChevronUpIcon className="h-5 w-5 transform rotate-90" />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum = page;
                if (page < 2) pageNum = i;
                else if (page > totalPages - 3) pageNum = totalPages - 5 + i;
                else pageNum = page - 2 + i;
                pageNum = Math.max(0, Math.min(pageNum, totalPages - 1));
                return (
                  <button key={pageNum} onClick={() => setPage(pageNum)} className={`relative inline-flex items-center px-4 h-9 border border-ink-300 text-body-sm font-mono tabular-nums font-medium ${page === pageNum ? 'z-10 bg-navy-900 border-navy-900 text-white' : 'bg-white text-ink-500 hover:bg-navy-50'}`}>
                    {pageNum + 1}
                  </button>
                );
              })}
              <button onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1} className={`relative inline-flex items-center px-2 h-9 border border-ink-300 bg-white text-body-sm font-medium ${page >= totalPages - 1 ? 'text-ink-300 cursor-not-allowed' : 'text-ink-500 hover:bg-navy-50'}`}>
                <span className="sr-only">Next</span>
                <ChevronDownIcon className="h-5 w-5 transform rotate-90" />
              </button>
              <button onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1} className={`relative inline-flex items-center px-2 h-9 rounded-r-md border border-ink-300 bg-white text-body-sm font-medium ${page >= totalPages - 1 ? 'text-ink-300 cursor-not-allowed' : 'text-ink-500 hover:bg-navy-50'}`}>
                <span className="sr-only">Last</span>»
              </button>
            </nav>
          </div>
          {/* Mobile pagination */}
          <div className="flex items-center justify-between w-full sm:hidden">
            <button onClick={() => setPage(page - 1)} disabled={page === 0} className={`relative inline-flex items-center px-4 h-9 border border-ink-300 text-body-sm font-medium rounded-md ${page === 0 ? 'text-ink-300 cursor-not-allowed' : 'text-ink-700 bg-white hover:bg-navy-50'}`}>
              Previous
            </button>
            <span className="text-body-sm text-ink-500 font-mono tabular-nums">Page {page + 1} of {totalPages}</span>
            <button onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1} className={`relative inline-flex items-center px-4 h-9 border border-ink-300 text-body-sm font-medium rounded-md ${page >= totalPages - 1 ? 'text-ink-300 cursor-not-allowed' : 'text-ink-700 bg-white hover:bg-navy-50'}`}>
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
export default DataTable;
